import type { GrantRole, Principal, PrincipalKind, SharingState } from './types';

// All names, teams and organisation units below are fictitious; head counts are invented.
export const ORG_UNITS: Principal[] = [
    { id: 'g-gbu', kind: 'gbu', name: 'GBU LEI', initials: 'LEI', detail: 'GBU · tous les territoires', tone: 'blue', members: 420 },
    { id: 'g-ter-idf', kind: 'territory', name: 'France Île-de-France', initials: 'IDF', detail: 'Territoire · GBU LEI', tone: 'purple', members: 86, parentId: 'g-gbu' },
    { id: 'g-ter-ne', kind: 'territory', name: 'France Nord-Est', initials: 'NE', detail: 'Territoire · GBU LEI', tone: 'purple', members: 64, parentId: 'g-gbu' },
    { id: 'g-ter-sud', kind: 'territory', name: 'France Sud', initials: 'SUD', detail: 'Territoire · GBU LEI', tone: 'purple', members: 72, parentId: 'g-gbu' },
    { id: 'g-fil-rc-idf', kind: 'filiere', name: 'Réseaux de chaleur IDF', initials: 'RC', detail: 'Filière · France Île-de-France', tone: 'green', members: 24, parentId: 'g-ter-idf' },
    { id: 'g-fil-rc-ne', kind: 'filiere', name: 'Réseaux de chaleur Nord-Est', initials: 'RC', detail: 'Filière · France Nord-Est', tone: 'green', members: 18, parentId: 'g-ter-ne' },
    { id: 'g-fil-rf-sud', kind: 'filiere', name: 'Réseaux de froid Sud', initials: 'RF', detail: 'Filière · France Sud', tone: 'green', members: 15, parentId: 'g-ter-sud' },
    { id: 'g-team-etudes', kind: 'team', name: 'Équipe Études IDF', initials: 'EI', detail: 'Équipe · Réseaux de chaleur IDF', tone: 'teal', members: 8, parentId: 'g-fil-rc-idf' },
    { id: 'g-team-com', kind: 'team', name: 'Équipe Commercial IDF', initials: 'CI', detail: 'Équipe · Réseaux de chaleur IDF', tone: 'teal', members: 6, parentId: 'g-fil-rc-idf' },
    { id: 'g-team-dev', kind: 'team', name: 'Équipe Développement Nord-Est', initials: 'DN', detail: 'Équipe · Réseaux de chaleur Nord-Est', tone: 'teal', members: 5, parentId: 'g-fil-rc-ne' },
    { id: 'g-team-ing', kind: 'team', name: 'Équipe Ingénierie Sud', initials: 'IS', detail: 'Équipe · Réseaux de froid Sud', tone: 'teal', members: 7, parentId: 'g-fil-rf-sud' },
];

export const USERS: Principal[] = [
    { id: 'u-jm', kind: 'user', name: 'Julie Moreau', initials: 'JM', detail: 'Équipe Études IDF · Réseaux de chaleur IDF', tone: 'blue', parentId: 'g-team-etudes' },
    { id: 'u-cb', kind: 'user', name: 'Camille Bernard', initials: 'CB', detail: 'Équipe Études IDF · Réseaux de chaleur IDF', tone: 'green', parentId: 'g-team-etudes' },
    { id: 'u-lp', kind: 'user', name: 'Louise Petit', initials: 'LP', detail: 'Équipe Commercial IDF · Réseaux de chaleur IDF', tone: 'pink', parentId: 'g-team-com' },
    { id: 'u-sf', kind: 'user', name: 'Sarah Fontaine', initials: 'SF', detail: 'Équipe Commercial IDF · Réseaux de chaleur IDF', tone: 'orange', parentId: 'g-team-com' },
    { id: 'u-am', kind: 'user', name: 'Alex Martin', initials: 'AM', detail: 'Équipe Développement Nord-Est · Réseaux de chaleur Nord-Est', tone: 'orange', parentId: 'g-team-dev' },
    { id: 'u-hl', kind: 'user', name: 'Hugo Lambert', initials: 'HL', detail: 'Équipe Développement Nord-Est · Réseaux de chaleur Nord-Est', tone: 'teal', parentId: 'g-team-dev' },
    { id: 'u-tr', kind: 'user', name: 'Thomas Roux', initials: 'TR', detail: 'Équipe Ingénierie Sud · Réseaux de froid Sud', tone: 'purple', parentId: 'g-team-ing' },
];

export const OWNER_ID = 'u-jm';

/** AO team that already exists from a previous tender: shows that AO teams are reusable. */
export const SEED_AO_TEAM: Principal = {
    id: 'ao-lille',
    kind: 'ao-team',
    name: 'AO Métropole Lilloise',
    initials: 'AO',
    detail: 'Équipe appel d’offres transversale',
    tone: 'orange',
    memberIds: ['u-am', 'u-tr', 'u-cb'],
};

export function initialState(scenario: 'private' | 'shared'): SharingState {
    return {
        ownerId: OWNER_ID,
        aoTeams: [{ ...SEED_AO_TEAM, memberIds: [...(SEED_AO_TEAM.memberIds ?? [])] }],
        requests: [],
        grants: scenario === 'private' ? [] : [
            { principalId: SEED_AO_TEAM.id, role: 'write' },
            { principalId: 'g-fil-rc-idf', role: 'read' },
        ],
    };
}

export const KIND_LABEL: Record<PrincipalKind, string> = {
    user: 'Utilisateur',
    'ao-team': 'Équipe AO',
    team: 'Équipe',
    filiere: 'Filière',
    territory: 'Territoire',
    gbu: 'GBU',
};

export const KIND_ICON: Record<PrincipalKind, string> = {
    user: 'person',
    'ao-team': 'workspace_premium',
    team: 'groups',
    filiere: 'account_tree',
    territory: 'map',
    gbu: 'corporate_fare',
};

export const ROLE_LABEL = { owner: 'Propriétaire', write: 'Peut modifier', read: 'Peut consulter' } as const;
export const GRANT_ROLES: GrantRole[] = ['write', 'read'];

/** Group kinds whose share reaches a whole territory or more: shown with a warning. */
export const BROAD_KINDS: PrincipalKind[] = ['territory', 'gbu'];
